export function normalizeUploadPath(rawPath) {
  let value = String(rawPath || '');
  if (value.startsWith('/')) value = value.slice(1);
  const parts = value.split('/');
  if (!value || value.includes('\\') || value.includes('\0')
      || parts.some(part => !part || part === '.' || part === '..')) {
    throw new Error(`Invalid relative path: ${rawPath || '(empty)'}`);
  }
  return parts.join('/');
}

export function uploadItem(file, relativePath = null) {
  const path = normalizeUploadPath(
    relativePath || file.webkitRelativePath || file._relPath || file.name,
  );
  return { file, path };
}

export function validateUploadItems(items) {
  if (!items.length) return ['No files were found.'];
  const errors = [];
  const paths = [];
  for (const item of items) {
    try {
      paths.push(normalizeUploadPath(item.path));
    } catch (error) {
      errors.push(error.message);
    }
  }
  const counts = new Map();
  for (const path of paths) counts.set(path, (counts.get(path) || 0) + 1);
  for (const [path, count] of counts) {
    if (count > 1) errors.push(`Duplicate destination path: ${path}`);
    const parts = path.split('/');
    for (let i = 1; i < parts.length; i += 1) {
      const parent = parts.slice(0, i).join('/');
      if (counts.has(parent)) {
        errors.push(`A file conflicts with a folder path: ${parent}`);
        break;
      }
    }
  }
  return [...new Set(errors)];
}

function fileFromEntry(entry) {
  return new Promise((resolve, reject) => {
    entry.file(resolve, () => reject(new Error(`Could not read ${entry.fullPath || entry.name}`)));
  });
}

function readDirectoryEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = [];
    const next = () => reader.readEntries(entries => {
      if (!entries.length) {
        resolve(all);
        return;
      }
      all.push(...entries);
      next();
    }, () => reject(new Error('Could not read a dropped directory.')));
    next();
  });
}

async function collectEntry(entry, out, onProgress) {
  if (entry.isFile) {
    const file = await fileFromEntry(entry);
    out.push(uploadItem(file, entry.fullPath || file.name));
    onProgress?.(out.length);
    return;
  }
  if (!entry.isDirectory) return;
  const children = await readDirectoryEntries(entry.createReader());
  for (const child of children) {
    await collectEntry(child, out, onProgress);
  }
}

export function captureDrop(dataTransfer) {
  const dataItems = Array.from(dataTransfer.items || [])
    .filter(item => !item.kind || item.kind === 'file');
  const entries = dataItems
    .map(item => item.webkitGetAsEntry?.())
    .filter(Boolean);
  if (entries.length && entries.length === dataItems.length) {
    return { entries, files: [] };
  }
  return { entries: [], files: Array.from(dataTransfer.files || []) };
}

export async function scanCapturedDrop(captured, onProgress) {
  if (!captured.entries.length) {
    return captured.files.map(file => uploadItem(file));
  }
  const out = [];
  for (const entry of captured.entries) {
    await collectEntry(entry, out, onProgress);
  }
  return out;
}

export function summarizeUploadItems(items) {
  return {
    fileCount: items.length,
    totalBytes: items.reduce((sum, item) => sum + (item.file.size || 0), 0),
  };
}

export function updateFileSelection(
  selectedPaths,
  orderedPaths,
  clickedPath,
  { checked, shiftKey = false, anchorPath = null } = {},
) {
  const available = new Set(orderedPaths);
  const next = new Set(selectedPaths.filter(path => available.has(path)));
  const clickedIndex = orderedPaths.indexOf(clickedPath);
  if (clickedIndex < 0) return { selectedPaths: [...next], anchorPath };

  const shouldSelect = checked ?? !next.has(clickedPath);
  const anchorIndex = shiftKey ? orderedPaths.indexOf(anchorPath) : -1;
  if (anchorIndex >= 0) {
    const start = Math.min(anchorIndex, clickedIndex);
    const end = Math.max(anchorIndex, clickedIndex);
    for (const path of orderedPaths.slice(start, end + 1)) {
      if (shouldSelect) next.add(path);
      else next.delete(path);
    }
  } else if (shouldSelect) {
    next.add(clickedPath);
  } else {
    next.delete(clickedPath);
  }

  return {
    selectedPaths: orderedPaths.filter(path => next.has(path)),
    anchorPath: clickedPath,
  };
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / (1024 ** index);
  return `${value.toFixed(index === 0 || value >= 10 ? 0 : 1)} ${units[index]}`;
}

export function formatTransferRate(bytesPerSecond) {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '';
  return `${formatBytes(bytesPerSecond)}/s`;
}

export function formatEta(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 60) return `${Math.max(1, Math.ceil(seconds))} sec left`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} min left`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);
  return minutes ? `${hours} hr ${minutes} min left` : `${hours} hr left`;
}
